import type { Editor } from "@tiptap/core";
import { DOMParser as ProseMirrorDOMParser } from "@tiptap/pm/model";

const AUTO_FOLLOW_THRESHOLD_PX = 160;

const findScrollContainer = (element: HTMLElement): HTMLElement => {
  let parent = element.parentElement;

  while (parent) {
    const { overflowY } = window.getComputedStyle(parent);
    if (
      (overflowY === "auto" || overflowY === "scroll") &&
      parent.scrollHeight > parent.clientHeight
    ) {
      return parent;
    }
    parent = parent.parentElement;
  }

  return (document.scrollingElement || document.documentElement) as HTMLElement;
};

const isNearBottom = (element: HTMLElement): boolean => {
  return (
    element.scrollHeight - element.scrollTop - element.clientHeight <=
    AUTO_FOLLOW_THRESHOLD_PX
  );
};

const followBottom = (element: HTMLElement): void => {
  element.scrollTop = element.scrollHeight;
};

/**
 * Applies only the changed ProseMirror document range. This keeps the editor
 * instance, selection, and surrounding DOM stable while generation is active.
 */
export const updateStreamingEditor = (editor: Editor, html: string): void => {
  const scrollContainer = findScrollContainer(editor.view.dom);
  const shouldAutoFollow = isNearBottom(scrollContainer);
  const container = document.createElement("div");
  container.innerHTML = html;

  try {
    const nextDocument = ProseMirrorDOMParser.fromSchema(editor.schema).parse(
      container
    );
    const start = editor.state.doc.content.findDiffStart(nextDocument.content);

    if (start === null) {
      return;
    }

    const end = editor.state.doc.content.findDiffEnd(nextDocument.content);
    const currentEnd = end?.a ?? editor.state.doc.content.size;
    const nextEnd = end?.b ?? nextDocument.content.size;
    const transaction = editor.state.tr
      .replace(start, currentEnd, nextDocument.slice(start, nextEnd))
      .setMeta("addToHistory", false)
      .setMeta("generationStream", true);

    editor.view.dispatch(transaction);
  } catch {
    // A final defensive fallback for unusual extension schemas. Streaming HTML
    // is balanced before this function is called, so this should be rare.
    editor.commands.setContent(html, false);
  }

  if (shouldAutoFollow) {
    window.requestAnimationFrame(() => followBottom(scrollContainer));
  }
};
